import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { ShoppingCart } from "lucide-react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import BookCard, { type Book } from "@/components/BookCard";
import BookDetailPanel from "@/components/BookDetailPanel";
import AuthRequiredModal from "@/components/AuthRequiredModal";
import KnygynasSkeletonLoader from "@/components/KnygynasSkeletonLoader";

import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useBooks } from "@/hooks/useBooks";

import { usePaymentVerification } from "@/hooks/usePaymentVerification";

const SITE = "https://www.laimesdieta.lt";

const Knygynas = () => {
  const { data: books = [], isLoading: loading } = useBooks();
  const { user: _user } = useAuth();

  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { count, cartOpen, setCartOpen } = useCart();
  const [searchParams] = useSearchParams();
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();

  usePaymentVerification();

  useEffect(() => {
    document.body.style.overflow = (selectedBook || cartOpen) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [selectedBook, cartOpen]);

  // Sync selected book from URL: slug param OR legacy ?book=<id>
  useEffect(() => {
    if (books.length === 0) return;

    if (slug) {
      const found = books.find((b) => b.slug === slug);
      if (found) {
        setSelectedBook(found);
      } else {
        navigate("/knygynas", { replace: true });
      }
      return;
    }

    const legacyId = searchParams.get("book");
    if (legacyId) {
      const found = books.find((b) => b.id === legacyId);
      if (found?.slug) {
        navigate(`/knygynas/${found.slug}`, { replace: true });
        return;
      }
      if (found) setSelectedBook(found);
      return;
    }

    setSelectedBook(null);
  }, [slug, searchParams, books, navigate]);

  const handleSelectBook = (book: Book) => {
    if (book.slug) {
      navigate(`/knygynas/${book.slug}`);
    } else {
      setSelectedBook(book);
    }
  };

  const handleClosePanel = () => {
    setSelectedBook(null);
    if (slug) navigate("/knygynas");
  };

  /* ──────── Group books into Netflix-style rows ────────
     Without an explicit genre column we derive rows from
     author + format. Result: one row per author, plus an
     E. knygos row if any ebooks exist. ──────────────── */
  const physical = books.filter((b) => b.type === "physical");

  const rows = useMemo(() => {
    const ebooks = books.filter((b) => b.type === "ebook");
    const physicalBooks = books.filter((b) => b.type === "physical");

    const result: { key: string; title: string; subtitle?: string; books: Book[] }[] = [];

    if (physicalBooks.length > 0) {
      result.push({ key: "naujausios", title: "KNYGOS", subtitle: "\n", books: physicalBooks });
    }
    if (ebooks.length > 0) {
      result.push({ key: "ebooks", title: "E. knygos", subtitle: "Skaityk iškart po pirkimo", books: ebooks });
    }
    return result;
  }, [books]);



  // Per-product SEO (when a book is selected via slug)
  const productHead = useMemo(() => {
    if (!selectedBook || !selectedBook.slug) return null;
    const url = `${SITE}/knygynas/${selectedBook.slug}`;
    const desc = (selectedBook.description || `${selectedBook.title} — įsigykite Laimės Dieta knygyne.`)
      .replace(/\s+/g, " ")
      .slice(0, 160);
    const isPreorder = !!selectedBook.is_preorder;
    const outOfStock =
      selectedBook.type === "physical" &&
      selectedBook.stock_quantity !== null &&
      (selectedBook.stock_quantity ?? 0) <= 0;
    const availability = isPreorder
      ? "https://schema.org/PreOrder"
      : outOfStock
      ? "https://schema.org/OutOfStock"
      : "https://schema.org/InStock";

    return {
      title: `${selectedBook.title} | Laimės Dieta knygynas`,
      desc,
      url,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Product",
        name: selectedBook.title,
        description: desc,
        ...(selectedBook.cover_url && { image: selectedBook.cover_url }),
        ...(selectedBook.author && {
          brand: { "@type": "Brand", name: selectedBook.author },
          author: { "@type": "Person", name: selectedBook.author },
        }),
        offers: {
          "@type": "Offer",
          price: selectedBook.price,
          priceCurrency: "EUR",
          availability,
          url,
        },
      },
    };
  }, [selectedBook]);

  if (loading) return <KnygynasSkeletonLoader />;

  return (
    <div className="relative pt-20">
      {productHead ? (
        <Helmet>
          <title>{productHead.title}</title>
          <meta name="description" content={productHead.desc} />
          <meta property="og:title" content={productHead.title} />
          <meta property="og:description" content={productHead.desc} />
          <meta property="og:type" content="product" />
          <meta property="og:url" content={productHead.url} />
          {selectedBook?.cover_url && (
            <meta property="og:image" content={selectedBook.cover_url} />
          )}
          <meta property="og:locale" content="lt_LT" />
          <meta name="twitter:card" content="summary_large_image" />
          <link rel="canonical" href={productHead.url} />
          <script type="application/ld+json">{JSON.stringify(productHead.jsonLd)}</script>
        </Helmet>
      ) : (
        <Helmet>
          <title>Knygynas | Laimės Dieta</title>
          <meta name="description" content="Knygos ir audio pamokos asmeniniam augimui. Naršyk Laimės Dieta knygyną pagal žanrus — Netflix stiliumi." />
          <meta property="og:title" content="Knygynas | Laimės Dieta" />
          <meta property="og:description" content="Knygos ir audio pamokos asmeniniam augimui." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`${SITE}/knygynas`} />
          <meta property="og:image" content="https://www.laimesdieta.lt/images/og-cover-2026-06-24.jpg" />
          <meta property="og:image:secure_url" content="https://www.laimesdieta.lt/images/og-cover-2026-06-24.jpg" />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:locale" content="lt_LT" />
          <link rel="canonical" href={`${SITE}/knygynas`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:image" content="https://www.laimesdieta.lt/images/og-cover-2026-06-24.jpg" />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BookStore",
            name: "Laimės Dieta Knygynas",
            url: `${SITE}/knygynas`,
            description: "Knygos asmeniniam augimui ir vaizduotei.",
            inLanguage: "lt",
            ...(physical.length > 0 && {
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Knygos",
                itemListElement: physical.map((book, i) => ({
                  "@type": "Book",
                  position: i + 1,
                  name: book.title,
                  author: { "@type": "Person", name: book.author || "Rimantė Miškinė" },
                  ...(book.cover_url && { image: book.cover_url }),
                  offers: {
                    "@type": "Offer",
                    price: book.price,
                    priceCurrency: "EUR",
                    availability: "https://schema.org/InStock",
                    url: book.slug ? `${SITE}/knygynas/${book.slug}` : `${SITE}/knygynas?book=${book.id}`,
                  },
                })),
              },
            }),
          })}</script>
        </Helmet>
      )}

      <BookDetailPanel
        book={selectedBook}
        onClose={handleClosePanel}
        onOpenCart={() => setCartOpen(true)}
      />

      <AuthRequiredModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />

      <section className="relative z-10 min-h-[calc(100vh-5rem)] py-10 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <header className="mb-8">
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-5">Knygynas</p>
            <h1 className="font-marquee text-3xl md:text-5xl font-bold text-ink tracking-tight">
              Knygos
            </h1>
            <p className="mt-3 text-ink/65 text-base md:text-lg max-w-2xl">
              Auginančios knygos — pristatome visame pasaulyje!
            </p>
          </header>
          {rows.length === 0 ? (
            <div className="text-center py-20 text-ink/55">Knygų dar nėra. Netrukus!</div>
          ) : (
            <div className="space-y-16">
              {rows.map((row) => (
                <div key={row.key}>
                  <h2 className="font-marquee text-2xl md:text-3xl font-bold text-ink tracking-tight mb-2">{row.title}</h2>
                  {row.subtitle?.trim() && (
                    <p className="text-ink/60 mb-6">{row.subtitle}</p>
                  )}
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
                    {row.books.map((b) => (
                      <BookCard key={b.id} book={b} onSelect={handleSelectBook} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

    </div>
  );
};

export default Knygynas;
